import { useMemo, useState } from "react";
import { styles } from "../styles";

// Checkbox-list equipment picker with a search box + bulk-select actions.
// Designed for facilities with thousands of units where the old
// scroll-through-everything pattern made selecting "all RTUs on roof B"
// painful. Search narrows the visible set; Select All Visible / Clear
// Visible operate on the FILTERED list (not the whole equipment list),
// so a typed "RTU" + Select All Visible adds only the matching units to
// the selection without touching the rest.
//
// Props:
//   equipment     — array of equipment objects ({ id, type, subType,
//                   modelNumber, serialNumber, manufacturer, ... })
//   selectedIds   — array of currently-selected equipment ids
//   onChange      — (newIds: string[]) => void
//   maxHeight     — optional scroll-container max-height (default 220px)
//   placeholder   — optional search input placeholder
export default function EquipmentMultiSelect({
  equipment = [],
  selectedIds = [],
  onChange,
  maxHeight = "220px",
  placeholder = "Search type, model, serial, manufacturer…",
}) {
  const [search, setSearch] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Lists sort alphabetically by default per Justin's general rule —
  // ingestion order is meaningless when the dispatcher's scanning for
  // a unit by name. Type is the primary key (RTU-1 next to RTU-2);
  // subType + model break ties so identical-type rows stay grouped.
  const sortedEquipment = useMemo(() => {
    const cmp = (a, b) => {
      const ta = (a.type || "").toLowerCase();
      const tb = (b.type || "").toLowerCase();
      if (ta !== tb) return ta.localeCompare(tb);
      const sa = (a.subType || "").toLowerCase();
      const sb = (b.subType || "").toLowerCase();
      if (sa !== sb) return sa.localeCompare(sb);
      const ma = (a.modelNumber || "").toLowerCase();
      const mb = (b.modelNumber || "").toLowerCase();
      return ma.localeCompare(mb);
    };
    return [...equipment].sort(cmp);
  }, [equipment]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sortedEquipment.filter((eq) => {
      if (showSelectedOnly && !selectedSet.has(eq.id)) return false;
      if (!term) return true;
      const hay = [
        eq.type,
        eq.subType,
        eq.modelNumber,
        eq.serialNumber,
        eq.manufacturer,
        eq.category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [sortedEquipment, search, showSelectedOnly, selectedSet]);

  const visibleIds = useMemo(() => visible.map((e) => e.id), [visible]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));

  const toggleOne = (id) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectAllVisible = () => {
    const merged = new Set([...selectedIds, ...visibleIds]);
    onChange([...merged]);
  };

  const clearVisible = () => {
    const visibleSet = new Set(visibleIds);
    onChange(selectedIds.filter((id) => !visibleSet.has(id)));
  };

  const totalCount = equipment.length;

  return (
    <div>
      {/* Search + counters */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "6px",
        }}
      >
        <input
          type="search"
          style={{ ...styles.input, flex: "1 1 220px", margin: 0 }}
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span
          style={{ fontSize: "12px", color: "#555", whiteSpace: "nowrap" }}
        >
          <strong>{selectedIds.length}</strong> selected
          {totalCount > 0 ? ` · ${visible.length}/${totalCount} shown` : ""}
        </span>
      </div>

      {/* Bulk actions */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "6px",
        }}
      >
        <button
          type="button"
          style={{ ...styles.button, margin: 0, fontSize: "12px" }}
          onClick={selectAllVisible}
          disabled={visibleIds.length === 0 || allVisibleSelected}
          title={
            allVisibleSelected
              ? "All matching equipment is already selected"
              : "Select every row currently shown"
          }
        >
          Select all visible
        </button>
        <button
          type="button"
          style={{ ...styles.button, margin: 0, fontSize: "12px" }}
          onClick={clearVisible}
          disabled={visibleIds.length === 0}
          title="Unselect every row currently shown"
        >
          Clear visible
        </button>
        <label
          style={{
            fontSize: "12px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: "#555",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          <input
            type="checkbox"
            checked={showSelectedOnly}
            onChange={(e) => setShowSelectedOnly(e.target.checked)}
          />
          Show only selected
        </label>
      </div>

      {/* Checkbox list */}
      <div
        style={{
          maxHeight,
          overflowY: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "6px 10px",
          background: "white",
        }}
      >
        {totalCount === 0 ? (
          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
            No equipment at this location.
          </p>
        ) : visible.length === 0 ? (
          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
            {showSelectedOnly
              ? "Nothing selected yet matches the search."
              : "No equipment matches your search."}
          </p>
        ) : (
          visible.map((eq) => {
            const checked = selectedSet.has(eq.id);
            return (
              <label
                key={eq.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  padding: "3px 0",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(eq.id)}
                />
                <span style={{ flex: 1 }}>
                  {eq.type}
                  {eq.subType ? ` (${eq.subType})` : ""}
                  {eq.modelNumber ? ` · ${eq.modelNumber}` : ""}
                  {eq.serialNumber ? ` · SN ${eq.serialNumber}` : ""}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
